import axios from 'axios';
import { supabase } from '../supabaseClient';

const BASE = window.location.hostname === 'localhost' 
  ? 'http://localhost:8000/api' 
  : (process.env.REACT_APP_API_URL || 'https://thebarbercraft-f91w.onrender.com/api');

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('barber_token', token);
  } else {
    localStorage.removeItem('barber_token');
  }
};

const getHeaders = () => {
  const token = localStorage.getItem('barber_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Add interceptor to handle 401s
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.warn("Unauthorized API call detected, clearing session...");
      localStorage.removeItem('barber_token');
      // Force Supabase to sign out as well to clear its internal storage
      await supabase.auth.signOut();
      // Optional: window.location.href = '/'; 
    }
    return Promise.reject(error);
  }
);

const api = {
  // Auth
  me: () => axios.get(`${BASE}/auth/me`, { headers: getHeaders() }).then(r => r.data),

  // Slots
  getSlots: (date, slotType) =>
    axios.get(`${BASE}/slots/available`, { params: { booking_date: date, slot_type: slotType }, headers: getHeaders() }).then(r => r.data),
  getBlockedDaysPublic: () =>
    axios.get(`${BASE}/blocked-days`, { headers: getHeaders() }).then(r => r.data),

  // Bookings
  createBooking: (data) =>
    axios.post(`${BASE}/bookings`, data, { headers: getHeaders() }).then(r => r.data),
  myBookings: () =>
    axios.get(`${BASE}/bookings`, { headers: getHeaders() }).then(r => r.data),
  cancelBooking: (id) =>
    axios.post(`${BASE}/bookings/${id}/cancel`, {}, { headers: getHeaders() }).then(r => r.data),
  rebook: (id, data) =>
    axios.post(`${BASE}/bookings/${id}/rebook`, data, { headers: getHeaders() }).then(r => r.data),

  // Payments
  createOrder: (bookingId) =>
    axios.post(`${BASE}/payments/create-order`, { booking_id: bookingId }, { headers: getHeaders() }).then(r => r.data),
  verifyPayment: (data) =>
    axios.post(`${BASE}/payments/verify`, data, { headers: getHeaders() }).then(r => r.data),

  // Wallet
  getWallet: () =>
    axios.get(`${BASE}/wallet`, { headers: getHeaders() }).then(r => r.data),
  useWallet: (bookingId, addonId) =>
    axios.post(`${BASE}/wallet/use`, { booking_id: bookingId, addon_service: addonId }, { headers: getHeaders() }).then(r => r.data),

  // Rewards
  getRewards: () =>
    axios.get(`${BASE}/rewards`, { headers: getHeaders() }).then(r => r.data),
  redeem: (rewardId) =>
    axios.post(`${BASE}/rewards/redeem`, { reward_id: rewardId }, { headers: getHeaders() }).then(r => r.data),
  myRedemptions: () =>
    axios.get(`${BASE}/rewards/my-redemptions`, { headers: getHeaders() }).then(r => r.data),
  applyRedemption: (redemptionId, bookingId) =>
    axios.post(`${BASE}/rewards/apply`, { redemption_id: redemptionId, booking_id: bookingId }, { headers: getHeaders() }).then(r => r.data),
  buySubscriptionOrder: () =>
    axios.post(`${BASE}/subscription/create-order`, {}, { headers: getHeaders() }).then(r => r.data),
  verifySubscription: (data) =>
    axios.post(`${BASE}/subscription/verify`, data, { headers: getHeaders() }).then(r => r.data),

  // Admin
  adminStats: () =>
    axios.get(`${BASE}/admin/stats`, { headers: getHeaders() }).then(r => r.data),
  adminBookings: (status, userId) =>
    axios.get(`${BASE}/admin/bookings`, { params: { ...(status && { status }), ...(userId && { user_id: userId }) }, headers: getHeaders() }).then(r => r.data),
  adminUpdateBooking: (id, status) =>
    axios.patch(`${BASE}/admin/bookings/${id}`, { status }, { headers: getHeaders() }).then(r => r.data),
  adminDeleteBooking: (id) =>
    axios.delete(`${BASE}/admin/bookings/${id}`, { headers: getHeaders() }).then(r => r.data),
  adminUsers: () =>
    axios.get(`${BASE}/admin/users`, { headers: getHeaders() }).then(r => r.data),
  adminUpdateWallet: (userId, balance) =>
    axios.patch(`${BASE}/admin/users/${userId}/wallet`, { balance }, { headers: getHeaders() }).then(r => r.data),
  adminUpdateRewards: (userId, points) =>
    axios.patch(`${BASE}/admin/users/${userId}/rewards`, { points }, { headers: getHeaders() }).then(r => r.data),
  adminUpdateMembership: (userId, data) =>
    axios.patch(`${BASE}/admin/users/${userId}/membership`, data, { headers: getHeaders() }).then(r => r.data),
  adminDeleteUser: (userId) =>
    axios.delete(`${BASE}/admin/users/${userId}`, { headers: getHeaders() }).then(r => r.data),
  processExpiredWallets: () =>
    axios.post(`${BASE}/admin/wallet/process-expired`, {}, { headers: getHeaders() }).then(r => r.data),
  
  // New Admin Features
  getBlockedDays: () =>
    axios.get(`${BASE}/admin/blocked-days`, { headers: getHeaders() }).then(r => r.data),
  blockDay: (date, reason) =>
    axios.post(`${BASE}/admin/blocked-days`, { date, reason }, { headers: getHeaders() }).then(r => r.data),
  unblockDay: (date) =>
    axios.delete(`${BASE}/admin/blocked-days/${date}`, { headers: getHeaders() }).then(r => r.data),
  addPoints: (userId, amount, points) =>
    axios.post(`${BASE}/admin/users/${userId}/rewards/add`, { amount, points }, { headers: getHeaders() }).then(r => r.data),
};

export default api;
